import {
  createBusinessPartnerSettlementRequestSchema,
  masterDataDateSchema,
  patchBusinessPartnerSettlementRequestSchema,
  type BusinessPartnerDetail,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useAddBusinessPartnerSettlement,
  usePatchBusinessPartnerSettlement,
  usePaymentMethods,
  usePaymentTerms,
} from '@/platform/features/master-data/use-master-data';

const successorSchema = patchBusinessPartnerSettlementRequestSchema.extend({
  effectiveFrom: masterDataDateSchema,
});

function validityText(validFrom: string | null, validTo: string | null): string {
  const from = validFrom ? `ab ${validFrom}` : '';
  const to = validTo ? `bis ${validTo}` : 'offen';
  return [from, to].filter(Boolean).join(' · ');
}

function toastServerError(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

export function PartnerSettlementSection({
  partnerId,
  settlements,
  canWrite,
}: {
  partnerId: string;
  settlements: BusinessPartnerDetail['settlements'];
  canWrite: boolean;
}) {
  const [successorId, setSuccessorId] = useState<string | null>(null);

  return (
    <section className="max-w-xl space-y-3">
      <h2 className="text-base font-semibold">Zahlungszuordnung</h2>
      {settlements.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Zahlungszuordnung.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {settlements.map((row) => (
            <li key={row.id} className="space-y-2">
              <p>
                {row.paymentTermCode} · {row.paymentMethodName}
                {` · ${validityText(row.validFrom, row.validTo)}`}
              </p>
              {canWrite && row.validTo == null && successorId !== row.id ? (
                <Button type="button" variant="outline" onClick={() => setSuccessorId(row.id)}>
                  Fortschreiben
                </Button>
              ) : null}
              {successorId === row.id ? (
                <SettlementForm
                  partnerId={partnerId}
                  settlement={row}
                  onDone={() => setSuccessorId(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canWrite && successorId == null ? <SettlementForm partnerId={partnerId} /> : null}
    </section>
  );
}

function SettlementForm({
  partnerId,
  settlement,
  onDone,
}: {
  partnerId: string;
  settlement?: BusinessPartnerDetail['settlements'][number];
  onDone?: () => void;
}) {
  const terms = usePaymentTerms();
  const methods = usePaymentMethods();
  const create = useAddBusinessPartnerSettlement(partnerId);
  const patch = usePatchBusinessPartnerSettlement(partnerId);
  const activeTerms = terms.data?.items.filter((item) => item.isActive) ?? [];
  const activeMethods = methods.data?.items.filter((item) => item.isActive) ?? [];
  const form = useForm({
    resolver: zodResolver(
      settlement ? successorSchema : createBusinessPartnerSettlementRequestSchema,
    ),
    defaultValues: {
      paymentTermId: settlement?.paymentTermId ?? activeTerms[0]?.id ?? '',
      paymentMethodId: settlement?.paymentMethodId ?? activeMethods[0]?.id ?? '',
      validFrom: '',
      effectiveFrom: '',
    },
  });

  async function onValid(values: {
    paymentTermId?: string;
    paymentMethodId?: string;
    validFrom?: string;
    effectiveFrom?: string;
  }) {
    try {
      if (settlement) {
        const parsed = successorSchema.parse(values);
        await patch.mutateAsync({ settlementId: settlement.id, body: parsed });
        toast.success('Zahlungszuordnung fortgeschrieben.');
        onDone?.();
      } else {
        const parsed = createBusinessPartnerSettlementRequestSchema.parse(values);
        await create.mutateAsync(parsed);
        toast.success('Zahlungszuordnung angelegt.');
        form.reset();
      }
    } catch (error) {
      toastServerError(error, 'Die Zahlungszuordnung konnte nicht gespeichert werden.');
    }
  }

  const pending = terms.isPending || methods.isPending || create.isPending || patch.isPending;

  return (
    <MasterDataListState
      pending={terms.isPending || methods.isPending}
      pendingLabel="Zahlungsregeln werden geladen …"
      error={terms.isError ? terms.error : methods.isError ? methods.error : null}
      errorLabel="Die Zahlungsregeln konnten nicht geladen werden."
      empty={
        !terms.isPending &&
        !methods.isPending &&
        (activeTerms.length === 0 || activeMethods.length === 0)
      }
      emptyLabel="Keine aktive Zahlungsbedingung oder Zahlungsart."
    >
      <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
        <div className="space-y-2">
          <Label htmlFor={`settlement-${settlement?.id ?? 'new'}-term`}>Zahlungsbedingung</Label>
          <Select
            value={form.watch('paymentTermId') ?? undefined}
            onValueChange={(value) => form.setValue('paymentTermId', value)}
          >
            <SelectTrigger id={`settlement-${settlement?.id ?? 'new'}-term`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeTerms.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`settlement-${settlement?.id ?? 'new'}-method`}>Zahlungsart</Label>
          <Select
            value={form.watch('paymentMethodId') ?? undefined}
            onValueChange={(value) => form.setValue('paymentMethodId', value)}
          >
            <SelectTrigger id={`settlement-${settlement?.id ?? 'new'}-method`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeMethods.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`settlement-${settlement?.id ?? 'new'}-from`}>Gültig ab</Label>
          <Input
            id={`settlement-${settlement?.id ?? 'new'}-from`}
            placeholder="JJJJ-MM-TT"
            {...form.register(settlement ? 'effectiveFrom' : 'validFrom')}
          />
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={pending}>
            {settlement ? 'Zuordnung fortschreiben' : 'Zuordnung anlegen'}
          </Button>
          {onDone ? (
            <Button type="button" variant="outline" onClick={onDone}>
              Abbrechen
            </Button>
          ) : null}
        </div>
      </form>
    </MasterDataListState>
  );
}
